import React from "react";
import { L as Link } from "../lib/i18n";
import { useLang } from "../contexts/LanguageContext";
import { SEO_BRAND_PATH_BY_LOCALE, slugify } from "../lib/seoSlug";
import { Mail, ShieldCheck, BadgeCheck, Clock, Phone } from "lucide-react";
import { COMPANY, RESPONSE_TIME_COPY } from "../config/company";

const FOOTER_I18N = {
  de: { dealers: "Händler", contact: "Kontakt", topBrands: "Top-Marken", supportHeading: "Support & Vertrauen", securePlatform: "Sichere Marktplatz-Kommunikation", verifiedDealers: "Verifizierte Schweizer Händler", chTitle: "Schweizer Fahrzeugmarktplatz", chSub: "Entwickelt und betrieben in der Schweiz." },
  fr: { dealers: "Concessionnaires", contact: "Contact", topBrands: "Top marques", supportHeading: "Support & confiance", securePlatform: "Messagerie marketplace sécurisée", verifiedDealers: "Concessionnaires suisses vérifiés", chTitle: "Marché automobile suisse", chSub: "Développé et exploité en Suisse." },
  it: { dealers: "Concessionari", contact: "Contatto", topBrands: "Top marche", supportHeading: "Supporto & fiducia", securePlatform: "Messaggistica marketplace sicura", verifiedDealers: "Concessionari svizzeri verificati", chTitle: "Marketplace di veicoli svizzero", chSub: "Sviluppato e gestito in Svizzera." },
  en: { dealers: "Dealers", contact: "Contact", topBrands: "Top brands", supportHeading: "Support & trust", securePlatform: "Secure marketplace messaging", verifiedDealers: "Verified Swiss dealers", chTitle: "Swiss vehicle marketplace", chSub: "Built and operated in Switzerland." },
};

// Curated short list for footer (highest Swiss used-car volume).
const FOOTER_TOP_BRANDS = ["BMW", "Mercedes-Benz", "Audi", "Volkswagen", "Tesla", "Porsche"];

export default function Footer() {
  const { t, lang } = useLang();
  const f = FOOTER_I18N[lang] || FOOTER_I18N.de;
  return (
    <footer className="mt-24 border-t border-zinc-800 bg-black">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-6 lg:px-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2">
            <svg width="28" height="28" viewBox="0 0 32 32" className="text-red-600" fill="currentColor" aria-hidden="true">
              <path d="M16 2 28 9v14L16 30 4 23V9z" />
              <path d="M16 11.5 22 15v6l-6 3.5-6-3.5v-6z" fill="#000" />
            </svg>
            <span className="font-display text-lg font-bold">Car<span className="text-red-600">Hive</span></span>
          </div>
          <p className="mt-4 max-w-xs text-sm text-zinc-500">{t.footer.tagline}</p>
          {/* Phase 55.1 — Top brands internal links for SEO crawl depth */}
          <div className="mt-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{f.topBrands}</p>
            <ul className="mt-3 flex flex-wrap gap-1.5" data-testid="footer-top-brands">
              {FOOTER_TOP_BRANDS.map((b) => {
                const seg = SEO_BRAND_PATH_BY_LOCALE[lang] || "autos";
                return (
                  <li key={b}>
                    <Link
                      to={`/${seg}/${slugify(b)}`}
                      className="inline-flex rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-1 text-[11px] text-zinc-300 transition hover:border-red-500/40 hover:text-red-300"
                      data-testid={`footer-brand-${slugify(b)}`}
                    >
                      {b}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.footer.links}</p>
          <ul className="mt-4 space-y-2 text-sm text-zinc-400">
            <li><Link to="/listings" className="hover:text-white">{t.nav.browse}</Link></li>
            <li><Link to="/sell" className="hover:text-white">{t.nav.sell}</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.footer.company}</p>
          <ul className="mt-4 space-y-2 text-sm text-zinc-400">
            <li><Link to="/dealers" className="hover:text-white">{f.dealers}</Link></li>
            <li><Link to="/contact" className="hover:text-white" data-testid="footer-link-contact">{f.contact}</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.footer.legal}</p>
          <ul className="mt-4 space-y-2 text-sm text-zinc-400">
            <li><Link to="/legal/imprint" className="hover:text-white" data-testid="footer-link-imprint">{t.footer.impressum}</Link></li>
            <li><Link to="/legal/privacy" className="hover:text-white" data-testid="footer-link-privacy">{t.footer.privacy}</Link></li>
            <li><Link to="/legal/terms" className="hover:text-white" data-testid="footer-link-terms">{t.footer.terms}</Link></li>
          </ul>
        </div>

        {/* Phase 71 — Trust column */}
        <div data-testid="footer-trust-block">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{f.supportHeading}</p>
          <ul className="mt-4 space-y-2.5 text-sm text-zinc-400">
            <li>
              <a
                href={`mailto:${COMPANY.emails.support}`}
                className="inline-flex items-center gap-2 break-all hover:text-white"
                data-testid="footer-support-email"
              >
                <Mail className="h-3.5 w-3.5 shrink-0 text-red-400" />
                <span className="truncate">{COMPANY.emails.support}</span>
              </a>
            </li>
            {COMPANY.hasPhone && COMPANY.phone && (
              <li>
                <a
                  href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 hover:text-white"
                  data-testid="footer-support-phone"
                >
                  <Phone className="h-3.5 w-3.5 shrink-0 text-red-400" />
                  <span>{COMPANY.phone}</span>
                </a>
              </li>
            )}
            <li className="inline-flex items-center gap-2 text-emerald-300/80">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              <span className="text-xs">{RESPONSE_TIME_COPY[lang] || RESPONSE_TIME_COPY.de}</span>
            </li>
            <li className="inline-flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
              <span>{f.securePlatform}</span>
            </li>
            <li className="inline-flex items-center gap-2">
              <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-sky-400" />
              <span>{f.verifiedDealers}</span>
            </li>
          </ul>
        </div>
      </div>
      <div
        className="mx-auto flex max-w-7xl flex-col items-center gap-1.5 px-4 py-5 text-center sm:px-6 lg:px-8"
        data-testid="footer-ch-hint"
      >
        <p className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400">
          <span aria-hidden="true">🇨🇭</span> {f.chTitle}
        </p>
        <p className="text-[11px] text-zinc-600">{f.chSub}</p>
      </div>
      <div className="border-t border-zinc-900 py-6 text-center text-xs text-zinc-600" data-testid="footer-copyright">
        © {new Date().getFullYear()} {COMPANY.name} – Alle Rechte vorbehalten
      </div>
    </footer>
  );
}
